import { ChartWindowArgs } from '@ardoq/graph';
import type { SetMouseHoverArgs, TimelineViewCommands } from './types';
import {
  setHoveredItem as setHoveredItemAction,
  setFocusedItem as setFocusedItemAction,
  collapseExpandGroup as collapseExpandGroupAction,
  collapseExpandAllGroups as collapseExpandAllGroupsAction,
  collapseExpandFieldData as collapseExpandFieldDataAction,
  collapseExpandAllFieldData as collapseExpandAllFieldDataAction,
  setChartWindowState,
  setUserDefinedLabelsColumnWidth,
  setResizeIsActive,
  setResizeIsInactive,
  setTimelineMouseHover,
  setTimelineChartWindowScrollPosition,
  chartAreaScrollTopChanged,
} from './actions';
import { dispatchAction } from '@ardoq/rxbeach';
import { DEFAULT_CHART_WINDOW_STATE } from './consts';

const setHoveredItem = (itemId: string | null, viewInstanceId: string) => {
  dispatchAction(
    setHoveredItemAction({ hoveredItemId: itemId }),
    viewInstanceId
  );
};

const setFocusedItem = (itemId: string | null, viewInstanceId: string) => {
  dispatchAction(
    setFocusedItemAction({ focusedItemId: itemId }),
    viewInstanceId
  );
};

const collapseExpandGroup = (
  rawItemIdPath: string,
  isCollapsed: boolean,
  viewInstanceId: string
) => {
  dispatchAction(
    collapseExpandGroupAction({ rawItemIdPath, isCollapsed }),
    viewInstanceId
  );
};

const collapseExpandAllGroups = (
  isCollapsed: boolean,
  viewInstanceId: string
) => {
  dispatchAction(
    collapseExpandAllGroupsAction({ isCollapsed }),
    viewInstanceId
  );
};

const collapseExpandFieldData = (
  rawItemIdPath: string,
  isCollapsed: boolean,
  viewInstanceId: string
) => {
  dispatchAction(
    collapseExpandFieldDataAction({ rawItemIdPath, isCollapsed }),
    viewInstanceId
  );
};

const collapseExpandAllFieldData = (
  isCollapsed: boolean,
  viewInstanceId: string
) => {
  dispatchAction(
    collapseExpandAllFieldDataAction({ isCollapsed }),
    viewInstanceId
  );
};

const setChartWindow = (
  chartWindow: ChartWindowArgs,
  viewInstanceId: string
) => {
  dispatchAction(setChartWindowState(chartWindow), viewInstanceId);
};

const resetChartWindow = (viewInstanceId: string) => {
  dispatchAction(
    setChartWindowState(DEFAULT_CHART_WINDOW_STATE),
    viewInstanceId
  );
};

const handleLabelsColumnResize = (
  containerStateBoxes: DOMRect[],
  viewInstanceId: string
) => {
  const labelsColumnState = containerStateBoxes[0];
  dispatchAction(
    setUserDefinedLabelsColumnWidth({
      userDefinedLabelsColumnWidth: labelsColumnState.width,
    }),
    viewInstanceId
  );
};

const setIsTimelineColumnsResizeActive = (
  isResizeActive: boolean,
  viewInstanceId: string
) => {
  // We separate resize active and inactive actions because we need to
  // debounce when the resize is deactivated
  if (isResizeActive) {
    dispatchAction(setResizeIsActive(), viewInstanceId);
    return;
  }
  dispatchAction(setResizeIsInactive(), viewInstanceId);
};

const setMouseHover = ({
  crosshairXPosition,
  graphNodeId,
  itemPhasesRowIndex,
  otherLabelIndex,
  popoverElementType,
  popoverY,
  popoverX,
  viewInstanceId,
}: SetMouseHoverArgs) => {
  dispatchAction(
    setTimelineMouseHover({
      crosshairXPosition,
      graphNodeId,
      itemPhasesRowIndex,
      otherLabelIndex,
      popoverElementType,
      popoverY,
      popoverX,
    }),
    viewInstanceId
  );
};
const setChartWindowScrollPosition = (
  scrollLeft: number,
  viewInstanceId: string
) => {
  dispatchAction(
    setTimelineChartWindowScrollPosition({ scrollLeft }),
    viewInstanceId
  );
};

const setChartAreaScrollTop = (
  e: React.UIEvent<HTMLDivElement, UIEvent>,
  viewInstanceId: string
) => {
  const scrollTop = e.currentTarget.scrollTop;
  dispatchAction(chartAreaScrollTopChanged({ scrollTop }), viewInstanceId);
};

export const commands: TimelineViewCommands = {
  resetChartWindow,
  setHoveredItem,
  setFocusedItem,
  collapseExpandGroup,
  collapseExpandAllGroups,
  collapseExpandFieldData,
  collapseExpandAllFieldData,
  setChartWindow,
  handleLabelsColumnResize,
  setIsTimelineColumnsResizeActive,
  setMouseHover,
  setChartWindowScrollPosition,
  setChartAreaScrollTop,
};
